import { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from '@mui/icons-material';
import {
  KnownDeliveryDtoDeliveryChannel,
  KnownDeliveryDtoDeliveryType
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { TimelineRow } from '../../../../ui/components/pure/timeline/timeline-row/timeline-row.component';
import { createPath, Paths } from '../../../../app/router/app.router';
import { deliveryTypeToIconMap } from '../../../deliveries/maps/delivery-type-to-icon.map';
import { deliveryChannelToIcon } from '../../../deliveries/maps/delivery-channel-to-icon.map';
import {
  LockerTimelineGroup,
  LockerTimelineSessionGroup,
  LockerTimelineDeliveryGroup,
  LockerTimelineUngroupedGroup,
  LockerTimelineEvent
} from '../logic/locker-timeline.utils';
import { useLockerTimelineGroupStyles } from './locker-timeline-group.styles';

const STATUS_BORDER_WIDTH = '3px';

function statusBorder(color: string): CSSProperties {
  return { borderLeft: `${STATUS_BORDER_WIDTH} solid ${color}` };
}

function statusBorderWithPadding(color: string): CSSProperties {
  return { borderLeft: `${STATUS_BORDER_WIDTH} solid ${color}`, paddingLeft: `calc(2rem - ${STATUS_BORDER_WIDTH})` };
}

function eventStatusBorder(color: string): CSSProperties {
  return {
    borderLeft: `${STATUS_BORDER_WIDTH} solid ${color}`,
    paddingLeft: `calc(2rem - ${STATUS_BORDER_WIDTH})`,
    paddingBottom: '1.4rem'
  };
}

function withLastEventDividerHidden(events: LockerTimelineEvent[]): LockerTimelineEvent[] {
  return events.map((event, index) =>
    index === events.length - 1
      ? { ...event, props: { ...event.props, options: { ...event.props.options, dividerStyle: 'none' } } }
      : event
  );
}

function DeliveryGroupSection({ group, projectID }: { group: LockerTimelineDeliveryGroup; projectID: string }) {
  const classes = useLockerTimelineGroupStyles();
  const events = withLastEventDividerHidden(group.events);
  const firstColor = group.events[0]?.statusColor;

  return (
    <div className={classes.deliveryWrapper}>
      <div className={classes.dashedDivider} style={statusBorder(firstColor)} />
      {group.deliveryLabel && (
        <div className={classes.deliveryHeader} style={statusBorder(firstColor)}>
          {group.deliveryType &&
            (() => {
              const DeliveryTypeIcon = deliveryTypeToIconMap[group.deliveryType as KnownDeliveryDtoDeliveryType];
              return DeliveryTypeIcon ? <DeliveryTypeIcon className={classes.groupIcon} /> : null;
            })()}
          {group.restricted ? (
            <span className={classes.disabledDeliveryLink}>#{group.deliveryDigitalSignature ?? group.deliveryId}</span>
          ) : (
            <Link
              to={createPath(Paths.delivery, { projectID, deliveryID: group.deliveryId })}
              className={classes.deliveryLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              #{group.deliveryDigitalSignature ?? group.deliveryId}
              <ChevronRight className={classes.chevronIcon} />
            </Link>
          )}
        </div>
      )}
      {events.map((event, index) => (
        <div key={index} className={classes.eventRowWrapper} style={eventStatusBorder(event.statusColor)}>
          <TimelineRow {...event.props} />
        </div>
      ))}
    </div>
  );
}

function SessionGroupSection({ group, projectID }: { group: LockerTimelineSessionGroup; projectID: string }) {
  const classes = useLockerTimelineGroupStyles();

  const allSections = [
    ...group.deliveryGroups.map((dg) => ({ type: 'delivery' as const, group: dg })),
    ...(group.ungroupedEvents.length > 0 ? [{ type: 'ungrouped' as const, events: group.ungroupedEvents }] : [])
  ];

  const firstEvent = group.deliveryGroups[0]?.events[0] ?? group.ungroupedEvents[0];
  const firstColor = firstEvent?.statusColor;

  return (
    <div className={classes.sessionWrapper}>
      <div className={classes.solidDivider} style={statusBorder(firstColor)} />
      <div className={classes.sessionHeader} style={statusBorder(firstColor)}>
        <div className={classes.sessionHeaderLeft}>
          {group.sessionChannel &&
            (() => {
              const ChannelIcon = deliveryChannelToIcon[group.sessionChannel as KnownDeliveryDtoDeliveryChannel];
              return ChannelIcon ? <ChannelIcon className={classes.groupIcon} /> : null;
            })()}
          {group.sessionUserDisplayName && (
            <span className={classes.sessionUserName}>{group.sessionUserDisplayName}</span>
          )}
        </div>
        <span className={classes.sessionLabel}>Session: {group.sessionId}</span>
      </div>
      {allSections.map((section) => {
        if (section.type === 'delivery') {
          return <DeliveryGroupSection key={section.group.deliveryId} group={section.group} projectID={projectID} />;
        }
        const ungroupedEvents = withLastEventDividerHidden(section.events as LockerTimelineEvent[]);
        return (
          <div key="ungrouped" className={classes.deliveryWrapper}>
            {ungroupedEvents.map((event, i) => (
              <div key={i} className={classes.eventRowWrapper} style={eventStatusBorder(event.statusColor)}>
                <TimelineRow {...event.props} />
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function UngroupedSection({ group }: { group: LockerTimelineUngroupedGroup }) {
  const classes = useLockerTimelineGroupStyles();
  const events = withLastEventDividerHidden(group.events);
  const firstColor = group.events[0]?.statusColor;

  return (
    <div className={classes.deliveryWrapper}>
      <div className={classes.solidDivider} style={statusBorder(firstColor)} />
      <div className={classes.ungroupedSpacer} style={statusBorderWithPadding(firstColor)} />
      {events.map((event, index) => (
        <div key={index} className={classes.eventRowWrapper} style={eventStatusBorder(event.statusColor)}>
          <TimelineRow {...event.props} />
        </div>
      ))}
    </div>
  );
}

export function LockerTimelineGroupComponent({ group, projectID }: { group: LockerTimelineGroup; projectID: string }) {
  if (group.type === 'session') {
    return <SessionGroupSection group={group} projectID={projectID} />;
  }

  if (group.type === 'ungrouped') {
    return <UngroupedSection group={group} />;
  }

  return <DeliveryGroupSection group={group} projectID={projectID} />;
}
